/* Pipeline panel: Execution Order view for calculations + global scripts */

import { state, signals, refs, esc, escAttr, setPanelLoading } from './state.js';
import { effect } from './store.js';
import { api } from './api.js';
import { getFormulaInfo } from './formula-data.js';
import { openFileTab } from './search.js';
import { pullWithFeedback } from './file-tree.js';
import { getAuthMode } from './auth.js';
import { humanizeEventName } from './humanize-event.js';

// v2 render branch: the v2 Execution order view upgrades group/field headers
// to real <button>s (keyboard + aria-expanded) and prettifies CamelCase event
// names; v1's #pipelineBody keeps its original div/caret markup byte-for-byte.
function _isV2(container) {
  return !!(container && container.closest && container.closest("#appV2"));
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

/**
 * Fetch + render the Execution Order tree (Item Calculations or Quote
 * Events) into `container`.
 *
 * `container` defaults to the v1 panel's #pipelineBody, so every existing
 * v1 call site (`loadPipeline()`, no args) is behaviorally unchanged.
 * Passing an explicit container (e.g. v2's #v2ExecMount inner body div)
 * fetches + renders the same real data into it; setPanelLoading() -- which
 * drives the v1 panel-header loading bar -- only fires for the v1 panel,
 * since a v2 container has no such chrome (`panel-exec-order` doesn't exist
 * there).
 *
 * `scope` is an optional override -- `"item-calcs"` or `"quote-events"`.
 * Omitted (every v1 call site), it falls back to the shared
 * `state.pipelineScope` (v1's own tab state), so v1 is behaviorally
 * unchanged. v2's exec view passes its own module-local scope (see
 * `renderExecOrderInto` below) so its toggle never touches v1's state.
 */
export async function loadPipeline(container = document.getElementById("pipelineBody"), scope) {
  const body = container;
  if (!body) return;
  if (state.pipelineLoading) return;
  const isV1Panel = body.id === "pipelineBody";

  state.pipelineLoading = true;
  if (isV1Panel) setPanelLoading('exec-order', true);
  // Hosted loads live from SAP (seconds, not the local cache's instant) — say so, matching
  // the search surfaces' latency copy. Local keeps its original placeholder.
  body.innerHTML = getAuthMode() === "hosted"
    ? `<div class="pipeline-placeholder">Loading execution order from SAP. This can take a few seconds.</div>`
    : `<div class="pipeline-placeholder">Loading execution order...</div>`;

  try {
    // scope: optional override (v2 exec view passes its own module-local
    // scope). Omitted (v1's no-arg call sites) preserves current behavior --
    // driven by the shared state.pipelineScope.
    const effScope = scope || state.pipelineScope || "item-calcs";

    if (effScope === "item-calcs") {
      // Use fields-tree endpoint for hierarchical view
      const data = await api.fieldsTree();
      state.fieldsTree = data;
      state.fieldsTreeSource = data.source;
      renderFieldsTree(body);
    } else {
      // Quote events — execution-order (per-connection in hosted; see api.executionOrder)
      const data = await api.executionOrder("quote");
      state.pipelineData = data;
      renderQuoteEvents(body);
    }
  } catch (err) {
    body.innerHTML = `<div class="pipeline-placeholder" style="color:var(--error)">Failed to load: ${esc(err.message)}</div>`;
  } finally {
    state.pipelineLoading = false;
    if (isV1Panel) setPanelLoading('exec-order', false);
  }
}

// v2's own scope choice -- deliberately module-local (NOT state.pipelineScope)
// so toggling it can never mutate v1's shared tab state. Resets to the
// default on every fresh page load (module re-evaluates); a tenant switch
// re-renders the header (see renderExecOrderInto below) but intentionally
// does NOT reset this back to "item-calcs" -- the user's chosen scope
// carries across a switch, same as v1's own state.pipelineScope does.
let _v2ExecScope = "item-calcs";

/**
 * v2 shell entry point: render a scope-tab header (Item Calculations / Quote
 * Events) + fetch and render the Execution Order tree into an arbitrary
 * container, e.g. #v2ExecMount. Thin wrapper around the now container-aware
 * loadPipeline() + initFormulaPopup() -- the v2 pane is read-only, so
 * drag-and-drop reorder is never wired for it (bindTreeInteractions only
 * binds it for the v1 #pipelineBody) and the formula-popup binding is
 * skipped too (initFormulaPopup only binds for the v1 #pipelineBody -- see
 * its own comment for why that can't just be a "#formulaDialog exists?"
 * check).
 *
 * The tab header is rebuilt (innerHTML) on every call -- including
 * subsequent calls after a tenant switch's guard reset -- so listeners never
 * pile up (the old buttons, and their listeners, are discarded with the old
 * markup). Switching scope re-calls loadPipeline(body, _v2ExecScope)
 * directly rather than deferring to switchPipelineScope()'s v1
 * already-cached short-circuit, so every v2 scope switch is a fresh fetch --
 * intentional (real data, no v2-side caching yet).
 */
export async function renderExecOrderInto(container) {
  if (!container) return;
  // Uniformity pass: the standard `.content-head` (title + stats sub + a
  // `.seg2` scope toggle in `.ch-right`) over a centered `.vpad` body — the
  // same chrome every other v2 view wears. The `.pipeline-scope` class stays
  // on the seg2 container purely as this function's wiring hook.
  container.innerHTML = `
    <div class="content-head">
      <span class="ch-title">Execution order</span>
      <span class="ch-sub"></span>
      <div class="ch-right">
        <div class="seg2 pipeline-scope v2-exec-scope">
          <button type="button" class="${_v2ExecScope === "item-calcs" ? "on" : ""}" data-scope="item-calcs">Item Calculations</button>
          <button type="button" class="${_v2ExecScope === "quote-events" ? "on" : ""}" data-scope="quote-events">Quote Events</button>
        </div>
      </div>
    </div>
    <div class="vpad"><div class="v2-exec-body"></div></div>`;
  // No id on this inner div -- M3's drag-drop/formula-popup gating
  // (container.id === "pipelineBody") must keep excluding v2.
  const body = container.querySelector(".v2-exec-body");
  container.querySelectorAll(".pipeline-scope button[data-scope]").forEach(t =>
    t.addEventListener("click", () => {
      if (t.dataset.scope === _v2ExecScope) return;
      _v2ExecScope = t.dataset.scope;
      container.querySelectorAll(".pipeline-scope button[data-scope]").forEach(x =>
        x.classList.toggle("on", x.dataset.scope === _v2ExecScope));
      loadPipeline(body, _v2ExecScope)
        .then(() => _syncExecSub(container, body))
        .catch(err => console.error("[v2] exec scope switch failed:", err));
    }));
  await loadPipeline(body, _v2ExecScope);
  _syncExecSub(container, body);
  initFormulaPopup(container);
}

// v2 only: the renderers append a `.pipeline-stats` footer line (v1-shared
// markup) — lift its text into the header's `.ch-sub`, where every other v2
// view keeps its meta, and drop the footer node.
function _syncExecSub(container, body) {
  const sub = container.querySelector(".content-head .ch-sub");
  if (!sub) return;
  const stats = body ? body.querySelector(".pipeline-stats") : null;
  sub.textContent = stats ? stats.textContent : "";
  if (stats) stats.remove();
}

/**
 * Refresh the Execution Order view if its panel is currently visible.
 * No-op when the panel is hidden (the existing lazy-load reloads it on open).
 */
export function maybeRefreshExecOrder() {
  const container = document.getElementById('panel-exec-order');
  if (!container || container.hidden || !container.isConnected) return;
  if (state.pipelineLoading) return;
  loadPipeline();
}

let _execOrderBindingWired = false;

/**
 * Wire the live Execution Order binding once (called from init()). Whenever
 * code on disk changes (codebaseRevision bumps on save/push/pull/file-watch),
 * the visible exec-order view refreshes automatically instead of going stale.
 */
export function initExecOrderLiveBinding() {
  if (_execOrderBindingWired) return;
  _execOrderBindingWired = true;
  effect(() => {
    signals.codebaseRevision.get(); // track
    maybeRefreshExecOrder();
  });
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

export function renderPipeline(container = document.getElementById("pipelineBody")) {
  const body = container;
  if (!body) return;

  const scope = state.pipelineScope || "item-calcs";

  if (scope === "item-calcs") {
    if (state.fieldsTree) {
      renderFieldsTree(body);
    } else {
      body.innerHTML = `<div class="pipeline-placeholder">Click Refresh to load fields tree</div>`;
    }
  } else {
    if (state.pipelineData) {
      renderQuoteEvents(body);
    } else {
      body.innerHTML = `<div class="pipeline-placeholder">Click Refresh to load quote events</div>`;
    }
  }
}

// ---------------------------------------------------------------------------
// Fields Tree (4-level hierarchical view)
// ---------------------------------------------------------------------------

// Group disclosure header. v1: the original div + text caret, byte-identical.
// v2: a real <button> on the `.sectlabel-toggle` recipe (shell-v2.css —
// aria-expanded drives the chevron; native Enter/Space comes with <button>).
function _groupHeaderHtml(v2, label, count, title) {
  const titleAttr = title ? ` title="${escAttr(title)}"` : '';
  if (v2) {
    return `<button type="button" class="pl-group-header sectlabel-toggle" aria-expanded="false"${titleAttr}>
        <span class="ic ic-sm ic-chevron-down"></span>
        <span class="pl-group-label">${esc(label)}</span>
        <span class="panel-group-count pl-group-count">${count}</span>
      </button>`;
  }
  return `<div class="panel-group-header pl-group-header">
      <span class="panel-caret pl-group-caret">&#9654;</span>
      <span>${esc(label)}</span>
      <span class="panel-group-count pl-group-count">${count}</span>
    </div>`;
}

function renderFieldsTree(container) {
  const tree = state.fieldsTree;
  if (!tree || !tree.groups || !tree.groups.length) {
    container.innerHTML = `<div class="pipeline-placeholder">No fields found</div>`;
    return;
  }
  const v2 = _isV2(container);

  let html = '';
  for (const group of tree.groups) {
    const fieldCount = group.fields ? group.fields.length : 0;
    let fieldsHtml = '';

    for (const field of (group.fields || [])) {
      const calcCount = field.calculations ? field.calculations.length : null;
      const countLabel = calcCount !== null ? calcCount : '...';
      const hasCalcs = field.calculations && field.calculations.length > 0;
      const isLoading = state.fieldCalcsLoading[field.id];

      let calcsHtml = '';
      if (field.calculations) {
        calcsHtml = renderFieldCalcs(field.id, field.calculations);
      } else if (isLoading) {
        calcsHtml = `<div class="pl-field-loading"><span class="pl-field-spinner"></span> Loading...</div>`;
      }

      // v2: the field row is a real <button> too (same keyboard reasoning as
      // the group header; it contains no nested interactive children).
      const fieldRow = v2
        ? `<button type="button" class="pl-field" data-field-id="${field.id}" aria-expanded="false">
          <span class="ic ic-sm ic-chevron-down pl-field-chevron"></span>
          <span class="pl-field-name">${esc(field.name)}</span>
          <span class="pl-field-count">${countLabel}</span>
        </button>`
        : `<div class="pl-field" data-field-id="${field.id}">
          <span class="panel-caret pl-field-caret">${hasCalcs || calcCount === null ? '&#9654;' : ''}</span>
          <span class="pl-field-name">${esc(field.name)}</span>
          <span class="pl-field-count">${countLabel}</span>
        </div>`;

      fieldsHtml += `<div class="pl-field-wrap" data-field-id="${field.id}">
        ${fieldRow}
        <div class="pl-field-calcs">${calcsHtml}</div>
      </div>`;
    }

    html += `<div class="pl-group">
      ${_groupHeaderHtml(v2, group.rowTypeLabel, fieldCount)}
      <div class="pl-group-items">${fieldsHtml}</div>
    </div>`;
  }

  // Stats
  html += `<div class="pipeline-stats">${tree.totalFields || 0} fields, ${tree.totalMappings || 0} mappings (${tree.source})</div>`;

  container.innerHTML = html;
  bindTreeInteractions(container);
}

function renderFieldCalcs(fieldId, calculations) {
  if (!calculations || !calculations.length) return '';
  const readonly = getAuthMode() === "hosted"; // hosted pipeline is read-only (no drag-reorder)
  let html = '';
  for (const calc of calculations) {
    const badge = calc.isCustom
      ? `<span class="pl-badge usr">USR</span>`
      : `<span class="pl-badge sys">SYS</span>`;
    const isCustom = calc.isCustom ? '1' : '0';
    const infoBtn = `<button class="pl-formula-btn" data-calc-name="${escAttr(calc.name)}" data-custom="${isCustom}" title="View formula">&lt;/&gt;</button>`;
    html += `<div class="pl-calc"${readonly ? '' : ' draggable="true"'} data-calc-id="${calc.id}" data-field-id="${fieldId}">
      ${readonly ? '' : '<span class="pl-drag-handle" title="Drag to reorder">&#x2807;</span>'}
      <span class="pl-rank">${calc.rank}</span>
      <span class="pl-name">${esc(calc.name)}</span>
      ${badge}
      ${infoBtn}
    </div>`;
  }
  return html;
}

// Flip the `.open` class AND, when the toggle is a v2 <button> carrying
// aria-expanded, keep that attribute honest (the chevron rotation keys off it).
function _toggleOpen(toggleEl, stateEl) {
  const open = stateEl.classList.toggle('open');
  if (toggleEl.hasAttribute('aria-expanded')) toggleEl.setAttribute('aria-expanded', open ? 'true' : 'false');
  return open;
}

function bindTreeInteractions(container) {
  // Level 1: group toggling
  container.querySelectorAll('.pl-group-header').forEach(header => {
    header.addEventListener('click', () => {
      _toggleOpen(header, header.parentElement);
    });
  });

  // Level 2: field expand/collapse + lazy loading
  container.querySelectorAll('.pl-field').forEach(fieldEl => {
    fieldEl.addEventListener('click', () => {
      const wrap = fieldEl.closest('.pl-field-wrap');
      const fieldId = parseInt(fieldEl.dataset.fieldId, 10);

      if (wrap.classList.contains('open')) {
        _toggleOpen(fieldEl, wrap);
        return;
      }

      _toggleOpen(fieldEl, wrap);

      // If source is "api" and no calculations loaded yet, lazy-load
      const calcsContainer = wrap.querySelector('.pl-field-calcs');
      if (state.fieldsTreeSource === 'api' && !calcsContainer.innerHTML.trim()) {
        lazyLoadFieldCalcs(fieldId, calcsContainer);
      }
    });
  });

  // Level 3: drag-and-drop on calculations (v1 #pipelineBody only, and not
  // hosted -- v2's Execution order view is read-only and never wires reorder).
  if (container.id === "pipelineBody" && getAuthMode() !== "hosted") bindDragAndDrop(container);
}

async function lazyLoadFieldCalcs(fieldId, container) {
  if (state.fieldCalcsLoading[fieldId]) return;
  state.fieldCalcsLoading[fieldId] = true;
  container.innerHTML = `<div class="pl-field-loading"><span class="pl-field-spinner"></span> Loading...</div>`;

  try {
    const result = await api.fieldCalculations(fieldId);
    const calcs = result.calculations || [];

    // Update state
    if (state.fieldsTree) {
      for (const group of state.fieldsTree.groups) {
        for (const field of group.fields) {
          if (field.id === fieldId) {
            field.calculations = calcs;
            break;
          }
        }
      }
    }

    container.innerHTML = renderFieldCalcs(fieldId, calcs);
    // Re-bind drag on new elements
    bindDragAndDrop(container.closest('.pipeline-body') || container.closest('#pipelineBody'));
  } catch (err) {
    container.innerHTML = `<div class="pl-field-loading" style="color:var(--error)">Failed: ${esc(err.message)}</div>`;
  } finally {
    delete state.fieldCalcsLoading[fieldId];
  }
}

// ---------------------------------------------------------------------------
// Drag-and-Drop (scoped within a single field)
// ---------------------------------------------------------------------------

function bindDragAndDrop(container) {
  if (!container) return;

  container.querySelectorAll('.pl-calc').forEach(calc => {
    calc.addEventListener('dragstart', onDragStart);
    calc.addEventListener('dragend', onDragEnd);
    calc.addEventListener('dragover', onDragOver);
    calc.addEventListener('dragleave', onDragLeave);
    calc.addEventListener('drop', onDrop);
  });
}

let _dragCalcId = null;
let _dragFieldId = null;

function onDragStart(e) {
  _dragCalcId = parseInt(e.currentTarget.dataset.calcId, 10);
  _dragFieldId = parseInt(e.currentTarget.dataset.fieldId, 10);
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', String(_dragCalcId));
  e.currentTarget.classList.add('dragging');
}

function onDragEnd(e) {
  e.currentTarget.classList.remove('dragging');
  // Clean up all drag-over indicators
  document.querySelectorAll('.drag-over-above, .drag-over-below').forEach(el => {
    el.classList.remove('drag-over-above', 'drag-over-below');
  });
  _dragCalcId = null;
  _dragFieldId = null;
}

function onDragOver(e) {
  const target = e.currentTarget;
  const targetFieldId = parseInt(target.dataset.fieldId, 10);

  // Only allow drop within the same field
  if (targetFieldId !== _dragFieldId) return;

  e.preventDefault();
  e.dataTransfer.dropEffect = 'move';

  // Determine above/below based on mouse position
  const rect = target.getBoundingClientRect();
  const midY = rect.top + rect.height / 2;

  target.classList.remove('drag-over-above', 'drag-over-below');
  if (e.clientY < midY) {
    target.classList.add('drag-over-above');
  } else {
    target.classList.add('drag-over-below');
  }
}

function onDragLeave(e) {
  e.currentTarget.classList.remove('drag-over-above', 'drag-over-below');
}

async function onDrop(e) {
  e.preventDefault();
  const target = e.currentTarget;
  const targetFieldId = parseInt(target.dataset.fieldId, 10);

  if (targetFieldId !== _dragFieldId) return;

  target.classList.remove('drag-over-above', 'drag-over-below');

  const dropCalcId = parseInt(target.dataset.calcId, 10);
  if (_dragCalcId === dropCalcId) return;

  // Determine insert position
  const rect = target.getBoundingClientRect();
  const insertBefore = e.clientY < (rect.top + rect.height / 2);

  // Build new order from DOM
  const fieldWrap = target.closest('.pl-field-wrap');
  const calcEls = fieldWrap.querySelectorAll('.pl-calc');
  const currentOrder = Array.from(calcEls).map(el => parseInt(el.dataset.calcId, 10));

  // Remove dragged item
  const newOrder = currentOrder.filter(id => id !== _dragCalcId);

  // Find target position
  const targetIdx = newOrder.indexOf(dropCalcId);
  const insertIdx = insertBefore ? targetIdx : targetIdx + 1;
  newOrder.splice(insertIdx, 0, _dragCalcId);

  // Optimistic UI update
  const calcsContainer = fieldWrap.querySelector('.pl-field-calcs');
  const fieldId = targetFieldId;

  // Find field in state to get calc details
  let fieldCalcs = [];
  if (state.fieldsTree) {
    for (const group of state.fieldsTree.groups) {
      for (const field of group.fields) {
        if (field.id === fieldId && field.calculations) {
          fieldCalcs = field.calculations;
          break;
        }
      }
    }
  }

  // Reorder in-memory
  const calcMap = {};
  for (const c of fieldCalcs) calcMap[c.id] = c;
  const reordered = newOrder.map((id, i) => ({ ...calcMap[id], rank: i + 1 })).filter(c => c.id);

  // Update DOM immediately
  calcsContainer.innerHTML = renderFieldCalcs(fieldId, reordered);
  bindDragAndDrop(calcsContainer.closest('.pipeline-body') || calcsContainer.closest('#pipelineBody'));

  // Save to server
  try {
    const result = await api.fieldReorder(fieldId, newOrder);
    // Update state with server response
    if (state.fieldsTree) {
      for (const group of state.fieldsTree.groups) {
        for (const field of group.fields) {
          if (field.id === fieldId) {
            field.calculations = result.calculations;
            break;
          }
        }
      }
    }
    // Green flash on success
    fieldWrap.querySelector('.pl-field').classList.add('pl-save-ok');
    setTimeout(() => fieldWrap.querySelector('.pl-field')?.classList.remove('pl-save-ok'), 800);
  } catch (err) {
    // Red flash + rollback
    fieldWrap.querySelector('.pl-field').classList.add('pl-save-err');
    setTimeout(() => fieldWrap.querySelector('.pl-field')?.classList.remove('pl-save-err'), 800);
    // Restore original order
    calcsContainer.innerHTML = renderFieldCalcs(fieldId, fieldCalcs);
    bindDragAndDrop(calcsContainer.closest('.pipeline-body') || calcsContainer.closest('#pipelineBody'));
  }
}

// ---------------------------------------------------------------------------
// Quote Events (unchanged from original)
// ---------------------------------------------------------------------------

function renderQuoteEvents(container) {
  const data = state.pipelineData;
  // Filter to events that have global_script or product_script items (skip eventId=-1)
  const events = (data.events || []).filter(ev => ev.eventId !== -1 && ev.items.length > 0);

  if (!events.length) {
    container.innerHTML = `<div class="pipeline-placeholder">No script event bindings found</div>`;
    return;
  }
  const v2 = _isV2(container);

  let html = '';
  for (const ev of events) {
    html += renderEventGroup(ev.eventName, ev.items, v2);
  }

  const stats = data.stats || {};
  html += `<div class="pipeline-stats">${stats.totalScripts || 0} scripts across ${stats.activeEvents || 0} events</div>`;

  container.innerHTML = html;
  bindGroupToggles(container);
}

function renderEventGroup(label, items, v2 = false) {
  const count = items.length;
  let itemsHtml = '';
  for (const item of items) {
    const isScript = item.type === "global_script" || item.type === "product_script";
    const badge = isScript
      ? `<span class="pl-badge script">SCR</span>`
      : item.isCustom
        ? `<span class="pl-badge usr">USR</span>`
        : `<span class="pl-badge sys">SYS</span>`;
    const infoBtn = isScript
      ? `<button class="pl-formula-btn pl-open-script" data-script-name="${escAttr(item.name)}" title="Open in editor">&lt;/&gt;</button>`
      : `<button class="pl-formula-btn" data-calc-name="${escAttr(item.name)}" data-custom="${item.isCustom ? '1' : '0'}" title="View formula">&lt;/&gt;</button>`;
    const inactive = item.isActive === false ? ' inactive' : '';
    const modules = item.importedModules && item.importedModules.length
      ? `<span class="pl-modules">uses: ${item.importedModules.map(m => esc(m)).join(", ")}</span>`
      : '';
    itemsHtml += `<div class="pl-item${inactive}" title="${escAttr(item.name)}${inactive ? ' (inactive)' : ''}">
      <span class="pl-rank">${item.rank}</span>
      <span class="pl-name">${esc(item.name)}${modules}</span>
      ${badge}
      ${infoBtn}
    </div>`;
  }

  // v2 prettifies CamelCase event names for display ("AfterAddingProductsTo
  // Quote" -> "After adding products to quote"), keeping the raw identifier
  // in the tooltip; v1 shows the raw name unchanged.
  const displayLabel = v2 ? humanizeEventName(label) : label;
  const tooltip = v2 && displayLabel !== label ? label : null;
  return `<div class="pl-group">
    ${_groupHeaderHtml(v2, displayLabel, count, tooltip)}
    <div class="pl-group-items">${itemsHtml}</div>
  </div>`;
}

function bindGroupToggles(container) {
  container.querySelectorAll('.pl-group-header').forEach(header => {
    header.addEventListener('click', () => {
      _toggleOpen(header, header.parentElement);
    });
  });
}

// ---------------------------------------------------------------------------
// Scope tab switching
// ---------------------------------------------------------------------------

export function switchPipelineScope(scope) {
  state.pipelineScope = scope;
  document.querySelectorAll('.pipeline-scope .tab').forEach(t => {
    t.classList.toggle('active', t.dataset.scope === scope);
  });
  // Always re-render (may need to load)
  const body = document.getElementById("pipelineBody");
  if (!body) return;

  if (scope === "item-calcs" && !state.fieldsTree) {
    loadPipeline();
  } else if (scope === "quote-events" && !state.pipelineData) {
    loadPipeline();
  } else {
    renderPipeline();
  }
}

// ---------------------------------------------------------------------------
// Pull All Calculations (bootstrap)
// ---------------------------------------------------------------------------

export async function pullAllCalculations() {
  const body = document.getElementById("pipelineBody");
  const btn = document.getElementById("btnPullAllCalcs");
  if (!body) return;

  if (btn) { btn.disabled = true; btn.textContent = "Pulling..."; }
  body.innerHTML = `<div class="pipeline-placeholder">Pulling all calculations from cloud...</div>`;

  try {
    const result = await api.post("/api/sync/pull-all/calculations");
    const count = result.count || 0;
    const items = result.items || [];
    const pulled = items.filter(i => i.status === "pulled");
    const skipped = items.filter(i => i.status === "skipped");

    let msg = `Pulled ${pulled.length} calculation${pulled.length !== 1 ? 's' : ''}`;
    if (skipped.length) msg += `, ${skipped.length} skipped`;

    body.innerHTML = `<div class="pipeline-placeholder" style="color:var(--success)">${esc(msg)}</div>`;

    // Reload pipeline data after pull
    setTimeout(() => loadPipeline(), 500);
  } catch (err) {
    body.innerHTML = `<div class="pipeline-placeholder" style="color:var(--error)">Pull failed: ${esc(err.message)}</div>`;
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = "Pull All"; }
  }
}

// ---------------------------------------------------------------------------
// Formula info popup
// ---------------------------------------------------------------------------

function _setVisible(id, visible) {
  const el = document.getElementById(id);
  if (el) el.hidden = !visible;
}

function showSysFormulaPopup(calcName) {
  const dialog = document.getElementById('formulaDialog');
  if (!dialog) return;

  const info = getFormulaInfo(calcName);

  document.getElementById('formulaCalcName').textContent = calcName;
  document.getElementById('formulaLevel').textContent = info ? info.level : 'System';
  document.getElementById('formulaCode').textContent = info
    ? info.formula
    : 'No formula data available for this calculation.';
  document.getElementById('formulaDesc').textContent = info
    ? info.description
    : 'This system calculation is not in the reference database.';

  _setVisible('formulaSysContent', true);
  _setVisible('formulaUsrContent', false);
  _setVisible('formulaLoading', false);
  _setVisible('formulaError', false);

  dialog.show(); // non-modal — keep the editor interactive while reading the formula
}

async function showUsrFormulaPopup(calcName) {
  const dialog = document.getElementById('formulaDialog');
  if (!dialog) return;

  document.getElementById('formulaCalcName').textContent = calcName;

  // Show loading state
  _setVisible('formulaSysContent', false);
  _setVisible('formulaUsrContent', false);
  _setVisible('formulaLoading', true);
  _setVisible('formulaError', false);

  // Reset editor link state
  const editorLink = document.getElementById('formulaOpenEditor');
  if (editorLink) {
    editorLink.textContent = 'Open in editor';
    editorLink.className = 'formula-editor-link';
    editorLink.onclick = null;
    // Remove any stale pull buttons
    editorLink.parentElement?.querySelectorAll('.formula-pull-btn').forEach(b => b.remove());
  }

  dialog.show(); // non-modal — keep the editor interactive while reading the formula

  try {
    const data = await api.calculationUsage(calcName);

    // Description — hide row entirely if empty
    const desc = (data.description || '').trim();
    if (desc) {
      document.getElementById('formulaUsrDesc').textContent = desc;
      _setVisible('formulaUsrDescRow', true);
    } else {
      _setVisible('formulaUsrDescRow', false);
    }

    // Level
    document.getElementById('formulaUsrLevel').textContent =
      data.rowTypeLabel || `RowType ${data.rowType}`;

    // Script preview (truncate to ~20 lines)
    const script = data.script || '';
    const lines = script.split('\n');
    const truncated = lines.length > 20;
    const preview = truncated
      ? lines.slice(0, 20).join('\n') + `\n\n... (${lines.length - 20} more lines)`
      : script || '(empty script)';
    document.getElementById('formulaUsrScript').textContent = preview;

    // "Open in editor" link
    const editorLink = document.getElementById('formulaOpenEditor');
    if (editorLink) {
      const filePath = `custom-calculations/${calcName}.py`;
      editorLink.textContent = 'Open in editor';
      editorLink.className = 'formula-editor-link';
      editorLink.onclick = async (e) => {
        e.preventDefault();
        if (_fileExistsInTree(filePath)) {
          dialog.close();
          openFileTab(filePath, `${calcName}.py`, 'python');
        } else {
          editorLink.textContent = 'File not synced locally.';
          editorLink.className = 'formula-editor-link formula-editor-notfound';
          const pullBtn = document.createElement('button');
          pullBtn.className = 'btn btn-xs formula-pull-btn';
          pullBtn.textContent = 'Pull from cloud';
          pullBtn.onclick = async () => {
            pullBtn.disabled = true;
            pullBtn.textContent = 'Pulling...';
            const ok = await pullWithFeedback('calculations', calcName, { reloadTree: true });
            if (ok) {
              dialog.close();
              openFileTab(filePath, `${calcName}.py`, 'python');
            } else {
              pullBtn.textContent = 'Retry pull';
              pullBtn.disabled = false;
            }
          };
          editorLink.after(pullBtn);
        }
      };
    }

    // Attached fields
    const fields = data.attachedToFields || [];
    const fieldsEl = document.getElementById('formulaUsrFields');
    if (fields.length) {
      fieldsEl.innerHTML = `<ul class="formula-field-list">${
        fields.map(f => `<li>${esc(f.fieldName || `Field #${f.fieldId}`)}</li>`).join('')
      }</ul>`;
      _setVisible('formulaUsrFieldsRow', true);
    } else {
      _setVisible('formulaUsrFieldsRow', false);
    }

    // Imported modules — always show, but as muted text when empty
    const imports = data.importedModules || [];
    const importsEl = document.getElementById('formulaUsrImports');
    if (imports.length) {
      importsEl.innerHTML = `<span class="formula-label">Imports</span><span class="formula-imports">${
        imports.map(m => `<span class="formula-import-tag">${esc(m)}</span>`).join('')
      }</span>`;
    } else {
      importsEl.textContent = 'No modules imported';
    }

    // Flags
    const flagsEl = document.getElementById('formulaUsrFlags');
    const flag = (on, label) =>
      `<span class="formula-flag"><span class="formula-flag-icon ${on ? 'on' : 'off'}">${on ? '\u2713' : '\u2717'}</span>${esc(label)}</span>`;
    flagsEl.innerHTML = [
      flag(data.isActive, 'Active'),
      flag(data.executeOnAddUpdateItem, 'Runs on Add/Update Item'),
      flag(data.executeOnDistribute, 'Runs on Distribute'),
    ].join('');

    _setVisible('formulaLoading', false);
    _setVisible('formulaUsrContent', true);
  } catch (err) {
    _setVisible('formulaLoading', false);
    const errEl = document.getElementById('formulaError');
    if (errEl) errEl.textContent = `Could not load calculation details: ${err.message}`;
    _setVisible('formulaError', true);
  }
}

function _fileExistsInTree(filePath) {
  const tree = state.fileTree;
  if (!tree?.children) return false;
  const parts = filePath.split('/');
  let nodes = tree.children;
  for (let i = 0; i < parts.length; i++) {
    const node = nodes.find(n => n.name === parts[i]);
    if (!node) return false;
    if (i === parts.length - 1) return true;
    nodes = node.children || [];
  }
  return false;
}

async function openScriptInEditor(scriptName) {
  const filePath = `global-scripts/${scriptName}.py`;
  if (_fileExistsInTree(filePath)) {
    openFileTab(filePath, `${scriptName}.py`, 'python');
  } else {
    const ok = await pullWithFeedback('scripts', scriptName, { reloadTree: true });
    if (ok) {
      openFileTab(filePath, `${scriptName}.py`, 'python');
    } else {
      const btn = document.querySelector(`.pl-open-script[data-script-name="${CSS.escape(scriptName)}"]`);
      if (btn) {
        let toast = btn.parentElement.querySelector('.pl-script-toast');
        if (!toast) {
          toast = document.createElement('span');
          toast.className = 'pl-script-toast';
          btn.after(toast);
        }
        toast.textContent = 'Not synced — pull failed';
        setTimeout(() => toast.remove(), 4000);
      }
    }
  }
}

let _formulaInited = false;

/**
 * Wire the formula-info popup's click delegation once, against `container`
 * (defaults to v1's #pipelineBody -- the existing no-arg call site is
 * unchanged). Gated to the v1 panel specifically, NOT merely on
 * #formulaDialog existing: that <dialog> is a sibling of #app/#appV2 in
 * index.html (not nested inside #app), so it survives bootShellV2()'s
 * #app.remove() and is technically reachable from the v2 DOM too --
 * "dialog present?" is not a reliable v1-vs-v2 signal. The v2 Execution
 * order view is read-only in M3 (no reorder, no formula popup, no Monaco
 * hookup yet -- those are later milestones), so a v2 container degrades to
 * a no-op here: the calc tree still renders, clicking a formula button just
 * does nothing. _formulaInited is only latched on a successful (v1) bind,
 * so a no-op v2 call can't block a later v1 bind (not that the two ever run
 * in the same session today -- getShell() picks exactly one shell per page
 * load).
 */
export function initFormulaPopup(container = document.getElementById('pipelineBody')) {
  if (_formulaInited) return;
  if (!container || container.id !== 'pipelineBody') return;

  const dialog = document.getElementById('formulaDialog');
  if (!dialog) return;

  _formulaInited = true;

  container.addEventListener('click', (e) => {
    const btn = e.target.closest('.pl-formula-btn');
    if (!btn) return;
    e.stopPropagation();

    // SCR scripts: open directly in editor (no popup)
    const scriptName = btn.dataset.scriptName;
    if (scriptName) {
      openScriptInEditor(scriptName);
      return;
    }

    const calcName = btn.dataset.calcName;
    const isCustom = btn.dataset.custom === '1';
    if (isCustom) {
      showUsrFormulaPopup(calcName);
    } else {
      showSysFormulaPopup(calcName);
    }
  });

  const closeBtn = document.getElementById('formulaCloseBtn');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      document.getElementById('formulaDialog')?.close();
    });
  }

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });
  // The dialog is non-modal (dialog.show()), so the browser does not close it on
  // Escape by itself; do it at document level while it is open.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && dialog.open) dialog.close();
  });
}

// ---------------------------------------------------------------------------
// Test hook (E2E determinism)
// ---------------------------------------------------------------------------

// Lets an E2E assert that v2's exec-scope toggle (_v2ExecScope above) never
// mutates v1's shared state.pipelineScope. Mirrors window.__test_shellV2
// (shell-v2.js) / window.__test_promotion (promotion-wizard.js) / the
// single-getter window.__test_getActiveSessionId (main.js).
window.__test_getPipelineScope = () => state.pipelineScope;
